import { useEffect, useState } from "react";

/**
 * Home-screen install — BUILD_PLAN 1.21, NFR-1, AC-15.
 *
 * AC-15 asks that the app install to the home screen and launch offline on
 * **both iOS Safari and Android Chrome**, and those two get there by different
 * routes:
 *
 *   Android Chrome fires `beforeinstallprompt`, which can be captured and
 *   replayed on a tap. That is the accelerator.
 *
 *   iOS Safari fires nothing and exposes no API at all. The only way in is
 *   Share → Add to Home Screen, done by hand. A prompt that waits for an event
 *   would therefore never appear on an iPhone, which is half the club.
 *
 * So this shows instructions when there is no event to use, rather than
 * nothing — the same reasoning as FR-NOTIF-5 about sharing: an API is an
 * accelerator, never the mechanism.
 *
 * It is dismissible and stays dismissed. A banner that reappears every Saturday
 * is worse than no banner.
 */

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "core-baddy.install-dismissed";

export function InstallPrompt(): React.JSX.Element | null {
  const [event, setEvent] = useState<InstallEvent | null>(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "true",
  );

  useEffect(() => {
    const capture = (e: Event) => {
      e.preventDefault();
      setEvent(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", capture);
    return () => window.removeEventListener("beforeinstallprompt", capture);
  }, []);

  // Already installed: `standalone` is the modern signal, and the non-standard
  // navigator flag is the one iOS actually sets.
  //
  // Optional-called because this component is the least important thing on the
  // screen and, unguarded, is capable of taking the whole app down with it: a
  // throw here happens during render, above everything else in the tree. Any
  // environment without `matchMedia` — an unusual webview, a test runner —
  // would show a blank page rather than a missing install banner.
  const installed =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as { standalone?: boolean }).standalone === true;

  if (installed || dismissed) return null;

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  // Nothing to offer and no instructions worth giving on a desktop browser.
  if (!event && !isIos) return null;

  function dismiss(): void {
    localStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  }

  return (
    <div className="install-prompt">
      <div>
        <strong>Add Core Baddy to your home screen</strong>
        <p className="muted small">
          {event
            ? "It opens instantly and works without signal."
            : // iOS gives no API, so the only honest thing is to say where the
              // button is.
              "Tap Share, then “Add to Home Screen”. It opens instantly and works without signal."}
        </p>
      </div>

      <div className="row">
        {event ? (
          <button
            className="primary"
            onClick={() => {
              void event.prompt().then(() =>
                event.userChoice.then(({ outcome }) => {
                  if (outcome === "accepted") setEvent(null);
                  else dismiss();
                }),
              );
            }}
          >
            Add
          </button>
        ) : null}
        <button className="link" onClick={dismiss}>
          Not now
        </button>
      </div>
    </div>
  );
}
